<script>
import StatusDot from '@/components/StatusDot.vue'
import Icon from '@/components/Icon.vue'
import TextExpander from '@/components/TextExpander.vue'
import EventCardServices from '@/components/EventCardServices.vue'
import timeOperations from '@/mixins/timeOperations.js'
import incidentMixin from '@/mixins/incident.js'
import { mapGetters } from 'vuex'

export default {
    name: 'EventCard',
    data() {
        return {
            defaultLayoutThresholds: [
                { name: 'l', value: 1, linesVisible: 8 },
                { name: 'm', value: 2, linesVisible: 4 },
                { name: 's', value: 3, linesVisible: 0 },
            ]
        }
    },
    mixins: [
        incidentMixin,
        timeOperations
    ],
    props: {
        event: {
            type: Object,
            required: true
        },
        type: {
            type: Number,
            default: undefined
        },
        count: {
            type: Number,
            default: 1
        }
    },
    components: {
        EventCardServices,
        Icon,
        StatusDot,
        TextExpander
    },
    computed: {
        ...mapGetters({
            config: 'config'
        }),
        currentLayout () {
            const layout = this.layoutThresholds.find((layout) => {
                return this.count <= layout.value
            })
            const lastLayout =
                this.layoutThresholds[this.layoutThresholds.length - 1]
            return layout || lastLayout
        },
        eventData () {
            if (this.isIncident) {
                return {
                    ...this.event.updates ? this.event.updates[0] : {},
                    ...this.event
                }
            } else {
                return this.event
            }
        },
        layoutThresholds () {
            return this.config.event_card_layout_thresholds ||
                this.defaultLayoutThresholds
        },
        linesVisibleWithoutExpanding () {
            return this.currentLayout.linesVisible
        },
        isIncident () {
            return this.type === 0 || this.event.event_type === 0
        },
        isMaintenance () {
            return this.type === 1 || this.event.event_type === 1
        },
        styleClassName () {
            const prefix = 'event-card'
            return `${prefix}--${this.currentLayout.name}`
        },
        eventType () {
            return this.isIncident ? 'incident' : 'maintenance'
        },
        eventIconName () {
            return this.isIncident
                ? this.incidentIconName(this.eventData)
                : 'maintenance'
        },
        eventTitle () {
            return this.isIncident
                ? this.incidentTitle(this.eventData)
                : this.event.title
        },
        isStatusCountersView () {
            return this.config.event_services_list_mode === 0
        },
        isServicesListView () {
            return this.config.event_services_list_mode !== 0
        }
    }
}
</script>

<template>
    <li
        class="event-card"
        :class="styleClassName"
    >
        <header class="event-card__header">
            <div class="event-card__header__title">
                <Icon :name="eventIconName" />
                <router-link
                    :to="{
                        name: eventType,
                        params: { id: event.id }
                    }"
                    target="_blank"
                >
                    {{ eventTitle }}
                </router-link>
            </div>
        </header>
        <TextExpander
            v-if="eventData.message && linesVisibleWithoutExpanding"
            :text="eventData.message"
            :visible-lines="linesVisibleWithoutExpanding"
            :id="event.id"
            class="event-card__body"
        />
        <footer
            class="event-card__footer"
            :class="{
                'event-card__footer--status-counters-view': isStatusCountersView,
                'event-card__footer--services-list-view': isServicesListView
            }"
        >
            <div v-if="eventData?.services?.length"
                class="event-card__footer__status-counters"
            >
                <EventCardServices :services="eventData.services" />
            </div>
            <div class="event-card__footer__time">
                <p class="event-card__footer__start-time">
                    <Icon
                        v-if="isIncident"
                        :name="incidentTypeIconName(eventData)"
                    />
                    {{ formatTimestamp(event.timestamp_start) }}
                </p>
                <p v-if="isMaintenance" class="event-card__footer_end-time">
                    {{ formatTimestamp(event.timestamp_end) }}
                </p>
            </div>
        </footer>
    </li>
</template>

<style scoped lang="scss">
.event-card {
    background-color: #FFFFFF;
    border-radius: 6px;

    &:not(:last-child) {
        margin-bottom: 0.625rem;
    }

    &__header {
        background-color: #F4F7FC;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 0.75rem;
        border-radius: 6px 6px 0 0;

        &__title {
            display: flex;
            align-items: center;
        }

        &__toggle-btn {
            cursor: pointer;

            :deep(svg) {
                width: 10px;
                height: 10px;
                font-size: 10px;
            }

            svg {
                width: 10px;
                height: 10px;
                font-size: 10px;
            }
        }

        .icon {
            margin-right: 1rem;
        }

        a {
            color: #0077AA;
            text-decoration: underline;
            font-size: 0.875rem;
            font-weight: 600;
            font-weight: 600;
            letter-spacing: -0.33px;
            line-height: 1.1875rem;
        }
    }

    &__body {
        padding: 1.5rem 1.5rem 0.5rem;
        font-size: 0.8125rem;
        line-height: 1.21875rem;
        font-weight: 500;
        color: #545454;

        :deep(.text) {
            margin: 0;
        }
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        padding: 0.25rem 1.5rem 1.25rem 1rem;

        &__time {
            height: 100%;
            color: #757575;
            font-size: 0.8125rem;

            p {
                display: flex;
                align-items: center;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;

                :deep(svg) {
                    transform: scale(0.5);
                }
            }
        }

        &--services-list-view {
            padding: 0.75rem 1rem;
        }
    }

    &--m {
        .event-card {
            &__body {
                padding: 1rem 1.5rem 0.5rem;
            }

            &__footer {
                padding: 0.25rem 1.5rem 0.75rem 1rem;
            }
        }
    }

    &--s {
        .event-card {
            &__header {
                padding: 1.125rem 1rem 0.75rem;
            }

            &__footer {
                padding: 0.375rem 1rem 0.5rem 0.75rem;
            }
        }

        :deep(.event-card-services__item) {
            margin-right: 0.5rem;
        }

        :deep(.event-card-services--services-list-view:not(.event-card-services--count-1)) {
            flex-direction: row;
            justify-content: flex-start;
            align-items: center;
            height: 100%;

            .event-card-services__item {
                margin: 0;

                &__label {
                    display: none;
                }

                &:not(:first-child) {
                    margin: 0;
                }
            }
        }
    }
}
</style>
